/* ===========================================================================
   TELL ME WHEN IT IS BACK.

   Lee Chen, 9 September 2026: when there is no incoming quantity — no active PO,
   or every incoming unit already allocated to other orders — stop selling the
   SKU and "display the message on product page accordingly".

   Sharon, same day: and let the customer leave an email, and give me a tab
   showing how many did.

   Until today the whole of that state was a DISABLED button reading UNAVAILABLE.
   The customer left, and the clearest demand signal this business has — someone
   who wanted a thing we could not sell them — was thrown away.

   WHAT DECIDES WHETHER THIS SHOWS. Shopify's own `variant.available`, emitted by
   snippets/rn-preorder-cap.liquid. That already accounts for the CONTINUE/DENY
   policy the pre-order service sets every 15 minutes, so there is ONE source of
   truth: when the service marks a SKU exhausted and Shopify starts refusing it,
   this appears on its own. No second rule to keep in step with the first.

   IT NEVER TOUCHES A BUYABLE VARIANT. The form is only ever inserted where the
   buy box has already given up, so the worst thing a bug in here can do is fail
   to offer a signup — never block a sale. That is the same reason the quantity
   cap corrects rather than intercepts.

   THE SERVICE DOES NOT SEND THE EMAIL and this copy must not imply a schedule.
   It says we will email you, because we will, once Klaviyo is connected — it
   does not say when. If the request cannot be recorded the endpoint returns an
   error and the customer is told it did not work, rather than being promised
   something nothing wrote down.
   =========================================================================== */
(function () {
  'use strict';

  var CFG = window.RN_NOTIFY;
  if (!CFG || !CFG.endpoint || !CFG.variants || !CFG.variants.length) return;

  var byId = {};
  for (var i = 0; i < CFG.variants.length; i++) byId[String(CFG.variants[i].id)] = CFG.variants[i];

  var bb = document.querySelector('[id^="rn-bb-"]');
  if (!bb) return;

  var STYLE_ID = 'rn-notify-style';
  if (!document.getElementById(STYLE_ID)) {
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent =
      '[id^="rn-bb-"] .rn-notify{margin:14px 0 0;padding:16px;border:1px solid var(--rn-line,#e0d9cd);' +
      'border-radius:var(--rn-radius,4px);background:var(--rn-shell,#f7f4ef)}' +
      '[id^="rn-bb-"] .rn-notify h4{margin:0;font-size:14.5px;font-weight:600;color:var(--rn-ink,#1a1712)}' +
      '[id^="rn-bb-"] .rn-notify p{margin:6px 0 0;font-size:13px;line-height:1.5;color:var(--rn-ink70,#5c554b)}' +
      '[id^="rn-bb-"] .rn-notify form{display:flex;gap:8px;margin:12px 0 0;flex-wrap:wrap}' +
      '[id^="rn-bb-"] .rn-notify input{flex:1 1 200px;min-width:0;font:inherit;font-size:14px;' +
      'padding:11px 12px;border:1px solid var(--rn-line,#e0d9cd);border-radius:var(--rn-radius,4px);' +
      'background:#fff;color:var(--rn-ink,#1a1712)}' +
      '[id^="rn-bb-"] .rn-notify input:focus{outline:2px solid var(--rn-ink,#1a1712);outline-offset:-1px}' +
      '[id^="rn-bb-"] .rn-notify button{appearance:none;cursor:pointer;font:inherit;font-size:12.5px;' +
      'font-weight:700;letter-spacing:.09em;text-transform:uppercase;padding:11px 18px;' +
      'border:1px solid var(--rn-ink,#1a1712);border-radius:var(--rn-radius,4px);' +
      'background:var(--rn-ink,#1a1712);color:var(--rn-bone,#faf8f4)}' +
      '[id^="rn-bb-"] .rn-notify button:hover{opacity:.87}' +
      '[id^="rn-bb-"] .rn-notify button[disabled]{opacity:.45;cursor:default}' +
      '[id^="rn-bb-"] .rn-notify .rn-notify-said{margin:10px 0 0;font-size:13px;line-height:1.5}' +
      '[id^="rn-bb-"] .rn-notify .rn-notify-said.bad{color:var(--rn-terracotta,#9f3d0e)}';
    document.head.appendChild(st);
  }

  /* SOLD OUT = Shopify says unavailable, OR there is nothing left to sell.
     26 September 2026. `available` alone missed a variant whose inventory
     policy is still CONTINUE with 0 on the shelf and no pre-order allocation:
     Shopify would still sell it, so no sign-up was offered, while the PDP had
     nothing to sell. Same rule as soldOut() in assets/rn-pdp-qty.js: stock
     (untracked = 0) plus allocation; an untracked variant with no allocation
     has no number to go on and is left to `available`. */
  function isSoldOut(v) {
    if (!v) return false;
    if (v.available === false) return true;
    var rem = v.preorderRemaining > 0 ? v.preorderRemaining : 0;
    if (v.stock === null || v.stock === undefined) return false;
    return Math.max(0, v.stock) + rem === 0;
  }

  function currentVariantId() {
    var input = bb.querySelector('form input[name="id"]');
    return input && input.value ? String(input.value) : null;
  }

  function build(v) {
    var box = document.createElement('div');
    box.className = 'rn-notify';
    box.setAttribute('data-rn-notify', '');

    var h = document.createElement('h4');
    h.textContent = 'Sold out for now';
    box.appendChild(h);

    var p = document.createElement('p');
    /* Accurate about WHY, because "out of stock" on a pre-order product reads as
       a mistake: the customer can see we are selling things that do not exist
       yet. Every unit on the water is spoken for — that is the true reason and
       it is also the more flattering one. */
    p.textContent = 'Every unit on its way is already allocated. Leave your email and '
                  + 'we will let you know as soon as the next shipment opens.';
    box.appendChild(p);

    var form = document.createElement('form');
    form.noValidate = true;
    form.innerHTML =
      '<input type="email" required placeholder="Email address" aria-label="Email address" autocomplete="email">' +
      '<button type="submit">Notify me</button>';
    box.appendChild(form);

    var said = document.createElement('p');
    said.className = 'rn-notify-said';
    said.setAttribute('role', 'status');
    said.hidden = true;
    box.appendChild(said);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('input');
      var btn = form.querySelector('button');
      var email = (input.value || '').trim();

      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        said.hidden = false;
        said.className = 'rn-notify-said bad';
        said.textContent = 'That does not look like an email address. Please check it.';
        input.focus();
        return;
      }

      btn.disabled = true;
      var wasLabel = btn.textContent;
      btn.textContent = 'Sending';
      said.hidden = true;

      window.fetch(CFG.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sku: v.sku,
          email: email,
          variantId: v.id,
          product: CFG.product,
          variant: v.title,
          source: 'pdp',
        }),
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, j: j }; }); })
        .then(function (res) {
          said.hidden = false;
          if (res.ok && res.j && res.j.ok) {
            said.className = 'rn-notify-said';
            said.textContent = res.j.status === 'already'
              ? 'You are already on the list for this one.'
              : 'Thank you. We will email you when it is back.';
            form.remove();
          } else {
            /* The endpoint refuses rather than pretending when it could not
               write the address down. Say so plainly. */
            said.className = 'rn-notify-said bad';
            said.textContent = 'We could not save that just now. Please try again in a moment.';
            btn.disabled = false;
            btn.textContent = wasLabel;
          }
        })
        .catch(function () {
          said.hidden = false;
          said.className = 'rn-notify-said bad';
          said.textContent = 'We could not save that just now. Please try again in a moment.';
          btn.disabled = false;
          btn.textContent = wasLabel;
        });
    });

    return box;
  }

  function sync() {
    try {
      var id = currentVariantId();
      var v = id ? byId[id] : null;
      var existing = bb.querySelector('[data-rn-notify]');

      /* Available, unknown, or no variant resolved: never show it, and clear any
         form left over from a previous colourway. */
      if (!v || !isSoldOut(v)) {
        if (existing) existing.remove();
        return;
      }
      if (existing && existing.getAttribute('data-for') === id) return;
      if (existing) existing.remove();

      var box = build(v);
      box.setAttribute('data-for', id);

      /* Directly under the buy button, where the customer has just been told no. */
      var atc = bb.querySelector('.bb-atc');
      var anchor = atc && atc.closest('form') ? atc.closest('form') : atc;
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(box, anchor.nextSibling);
      else bb.appendChild(box);
    } catch (e) { /* never let this break a page that can still sell */ }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync, { once: true });
  } else {
    sync();
  }

  /* Every variant change on every PDP -- swatch, size, Reya's grid, the
     "More Than One Shape" links, the page-load resolution -- ends in this
     event (rn-pdp-variant.js, and the grid script in rn-buybox.liquid). The
     attribute watcher below never saw most of them: scripts set the field's
     .value, which does not change its value ATTRIBUTE. Kept anyway, with the
     click listener, as belt and braces. */
  document.addEventListener('rn:bb-variant', function () { setTimeout(sync, 0); });

  /* The buy box rewrites the hidden variant id as the customer changes size,
     chaise or colourway. */
  try {
    var input = bb.querySelector('form input[name="id"]');
    if (input && window.MutationObserver) {
      new MutationObserver(function () { setTimeout(sync, 40); })
        .observe(input, { attributes: true, attributeFilter: ['value'] });
    }
  } catch (e) {}
  bb.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.bb-sw, .bb-opt, .bb-cfgopt')) {
      setTimeout(sync, 90);
    }
  }, true);
})();
